import type { RefObject } from 'react';
import { useLayoutEffect } from 'react';
import type MapView from 'react-native-maps';

import { log, withSinglePinBoundsOffsetsCoordinates } from '../../utils';
import { getPinsBounds } from '../../helpers';
import type { GoogleMapRef, MapProps } from '../../types';
import { useNotMinimizedPinsRef } from '../useNotMinimizedPinsRef';

// ─────────────────────────────────────────────────────────────────────────────

export const useMapAutoBounds = (props: UseMapOffsetProps) => {
  const { isEnabled, mapRef, region, pins } = props;

  const pinsRef = useNotMinimizedPinsRef(pins);

  // fit all the pins on the active region change
  useLayoutEffect(() => {
    if (!isEnabled) return;

    if (!mapRef) {
      log.error('`map` object was not found!');

      return;
    }

    const bounds = getPinsBounds(
      withSinglePinBoundsOffsetsCoordinates(pinsRef.current),
    );

    if (!bounds || bounds.isEmpty()) return;

    mapRef?.fitBounds?.(bounds);
  }, [isEnabled, mapRef, pinsRef, region]);
};

// ─── Types ───────────────────────────────────────────────────────────────────

export type UseMapOffsetProps = Readonly<{
  isEnabled: boolean;
  mapRef?: GoogleMapRef['map_'];
  nativeMapRef?: RefObject<MapView>;
}> &
  Pick<MapProps, 'region' | 'offsetY' | 'pins'>;
